import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";

import agent from "../../utils/axios";
import { isAdmin } from "../../utils/auth";
import { formatMailingAddress } from "../../utils/address";
import { visuallyHiddenSx } from "../../utils/visuallyHidden";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useTaxIdReveal } from "../../hooks/useTaxIdReveal";
import {
  Callout,
  FilterBar,
  MaskedTaxId,
  PageHeader,
  PayeeCell,
  RevealAllButton,
  SearchField,
  StatusChip,
  payableMeta,
} from "../../components/ui";

/** One row of GET /api/Payees. The tax ID is passed to MaskedTaxId untouched. */
type PayeeRow = {
  id: string;
  name?: {
    first?: string | null;
    middle?: string | null;
    last?: string | null;
  } | null;
  mailingAddress?: {
    address1?: string | null;
    address2?: string | null;
    address3?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  phone?: {
    home?: string | null;
    cell?: string | null;
    work?: string | null;
  } | null;
  tin?: string | null;
  email?: string | null;
  payable?: boolean | null;
  reasonNotPayable?: string | null;
};

type Order = "asc" | "desc";

/** Server sort keys: real Mongo paths, so every sortable header actually sorts. */
// "name" as before AB#6872: Mongo sorts the embedded name document (effectively by first
// name), so the Payee header orders rows exactly as the old Name header did.
const SORT_PAYEE = "name";
const SORT_CONTACT = "email";
const SORT_ACH = "payable";

const ROWS_PER_PAGE_OPTIONS = [50, 100, 250];
const SEARCH_DEBOUNCE_MS = 300;
const COLUMN_COUNT = 5;

const payeeName = (row: PayeeRow): string =>
  [row.name?.first, row.name?.last]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");

function ContactCell({ row }: { row: PayeeRow }) {
  const phones = [
    row.phone?.cell ? `Cell ${row.phone.cell}` : "",
    row.phone?.home ? `Home ${row.phone.home}` : "",
  ].filter(Boolean);
  const address = formatMailingAddress(row.mailingAddress);
  const small = { fontSize: "0.75rem", color: "text.secondary" } as const;

  return (
    <Box sx={{ display: "grid", minWidth: 0, lineHeight: 1.4 }}>
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {row.email || (
          <Box component="span" sx={{ color: "text.secondary" }}>
            No email
          </Box>
        )}
      </Box>
      {phones.length > 0 && (
        <Box component="span" sx={small}>
          {phones.join(" · ")}
        </Box>
      )}
      {address && (
        <Box
          component="span"
          sx={{ ...small, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {address}
        </Box>
      )}
    </Box>
  );
}

function AchCell({ row }: { row: PayeeRow }) {
  const meta = payableMeta(row.payable, row.reasonNotPayable);
  return (
    <Tooltip title={row.payable === false ? meta.label : ""} placement="top">
      <Box sx={{ maxWidth: 260, display: "inline-flex", minWidth: 0 }}>
        <StatusChip meta={meta} size="small" />
      </Box>
    </Tooltip>
  );
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton width={140} />
            <Skeleton width={180} />
          </TableCell>
          <TableCell>
            <Skeleton width={200} />
            <Skeleton width={160} />
          </TableCell>
          <TableCell>
            <Skeleton width={90} />
          </TableCell>
          <TableCell>
            <Skeleton width={110} />
          </TableCell>
          <TableCell align="right">
            <Skeleton width={64} sx={{ ml: "auto" }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function PayeesTable() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [order, setOrder] = useState<Order>("asc");
  // Today's default key; it matches no field, so the server keeps its natural order.
  const [orderBy, setOrderBy] = useState("payeeId");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState<PayeeRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const searchTerm = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // D1: only Admin may reveal a tax ID.
  const canReveal = useMemo(() => isAdmin(), []);
  const reveal = useTaxIdReveal();
  const requestSeq = useRef(0);

  const lastSearch = useRef(searchTerm);

  useEffect(() => {
    // A new search starts from the first page (fetch once, from page 0).
    if (lastSearch.current !== searchTerm) {
      lastSearch.current = searchTerm;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    agent.Payees.getAllPayees(page, rowsPerPage, orderBy, order, searchTerm)
      .then((response: { data?: PayeeRow[]; count?: number }) => {
        if (seq !== requestSeq.current) return;
        setData(response?.data ?? []);
        setTotalCount(response?.count ?? 0);
        setLoadError(false);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setData([]);
        setTotalCount(0);
        setLoadError(true);
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [page, rowsPerPage, orderBy, order, searchTerm, reloadKey]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);

  const sortLabel = (key: string, label: string) => (
    <TableSortLabel
      active={orderBy === key}
      direction={orderBy === key ? order : "asc"}
      onClick={() => handleRequestSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  const showSkeleton = loading && data.length === 0 && !loadError;

  return (
    <Card>
      <FilterBar>
        <SearchField
          value={searchInput}
          onChange={setSearchInput}
          label="Search payees"
          placeholder="Search by payee ID, name, address, phone or email"
          width={420}
        />
      </FilterBar>
      <Box sx={{ height: 4 }}>
        {loading && <LinearProgress aria-label="Loading payees" />}
      </Box>
      <TableContainer>
        <Table size="small" aria-label="Payees">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === SORT_PAYEE ? order : false}>
                {sortLabel(SORT_PAYEE, "Payee")}
              </TableCell>
              <TableCell
                sortDirection={orderBy === SORT_CONTACT ? order : false}
              >
                {sortLabel(SORT_CONTACT, "Contact")}
              </TableCell>
              <TableCell sortDirection={orderBy === SORT_ACH ? order : false}>
                {sortLabel(SORT_ACH, "ACH")}
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Tax ID
                  {canReveal && (
                    <RevealAllButton
                      revealAll={reveal.revealAll}
                      onToggle={reveal.toggleAll}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box component="span" sx={visuallyHiddenSx}>
                  Open
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton && <SkeletonRows count={8} />}
            {!showSkeleton &&
              data.map((row, index) => {
                const name = payeeName(row);
                return (
                  <TableRow key={row.id ?? index} hover>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <PayeeCell
                        name={name || "Unnamed payee"}
                        id={row.id}
                        to={`/payees/detail/${row.id}`}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 340 }}>
                      <ContactCell row={row} />
                    </TableCell>
                    <TableCell>
                      <AchCell row={row} />
                    </TableCell>
                    <TableCell>
                      <MaskedTaxId
                        value={row.tin}
                        canReveal={canReveal}
                        revealed={canReveal ? reveal.isRevealed(row.id) : false}
                        onToggle={() => reveal.toggleRow(row.id)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={RouterLink}
                        to={`/payees/detail/${row.id}`}
                        variant="outlined"
                        size="small"
                        endIcon={<ChevronRight />}
                        aria-label={`Open ${name || "payee"} ${row.id}`}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            {!loading && loadError && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={{ py: 4 }}>
                  <Callout
                    tone="err"
                    role="alert"
                    title="Couldn't load payees"
                    action={
                      <Button color="inherit" size="small" onClick={retry}>
                        Retry
                      </Button>
                    }
                  >
                    Please retry, or contact support if this continues.
                  </Callout>
                </TableCell>
              </TableRow>
            )}
            {!loading && !loadError && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                  <Typography color="text.secondary">
                    {searchTerm
                      ? `No payees match "${searchTerm}".`
                      : "No payees yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Card>
  );
}

function PayeesList() {
  return (
    <React.Fragment>
      <PageHeader
        title="Payees"
        subtitle="Everyone CSI Pay pays: applicants, facilities and trainers."
        breadcrumbs={[{ label: "Payments" }, { label: "Payees" }]}
      />
      <PayeesTable />
    </React.Fragment>
  );
}

export default PayeesList;
